<template>
    <div class="AttributeInformation q-pa-sm">
        <div class="row items-center q-mb-sm">
            <div class="text-primary">
                {{ $t('visualization.customPlotPanels.attributeInfo') }}
            </div>
        </div>

        <div class="row q-col-gutter-md">
            <div class="col-12 col-md-auto AttributeInformationOptions">
                <div>
                    <QSelect
                        v-model="attribute"
                        dense
                        class="attrSelect"
                        :options="optionAttributes.map(i => i.name)"
                        :label="$t('visualization.customPlotPanels.attributesInfo.selectAttribute')"
                        @update:modelValue="getData"
                    />
                    <QSelect
                        ref="activities"
                        v-model="activities"
                        dense
                        class="Field"
                        :label="$t('visualization.customPlotPanels.attributesInfo.eventAttribute')"
                        :multiple="true"
                        :options="optionActivities"
                        :disable="!attribute"
                        @update:modelValue="handleStandardActivitiesInput"
                    >
                        <template #option="scope">
                            <QItem v-bind="scope.itemProps">
                                <QItemSection>
                                    <QItemLabel>
                                        {{ scope.opt }}
                                    </QItemLabel>
                                </QItemSection>
                                <QItemSection side>
                                    <QToggle
                                        v-model="scope.selected"
                                        :val="scope.opt"
                                        @update:modelValue="handleActivitiesInput(scope.opt)"
                                    />
                                </QItemSection>
                            </QItem>
                        </template>
                        <template v-if="activities.length > 0" #append>
                            <q-icon name="cancel" class="cursor-pointer" @click.stop.prevent="resetActivities()" />
                        </template>
                    </QSelect>
                </div>
                <div
                    v-if="attribute && !emptyRows && !numericAttribute"
                    class="column q-ma-xs q-gutter-sm"
                >
                    <div class="flex">
                        <div class="text-caption q-mr-sm">
                            {{ $t('visualization.customPlotPanels.attributesInfo.sortBy') }}
                        </div>
                        <QBtnToggle
                            v-model="currentSel.value"
                            toggleColor="primary"
                            textColor="primary"
                            noCaps
                            outline
                            unelevated
                            dense
                            size="0.9em"
                            padding="2px 10px"
                            :options="freqDur"
                            class="border-toggle"
                            @update:modelValue="getData"
                        />
                    </div>
                    <div class="flex">
                        <div class="text-caption q-mr-sm">
                            {{ $t('visualization.customPlotPanels.attributesInfo.valueType') }}
                        </div>
                        <QBtnToggle
                            v-model="optionSel"
                            toggleColor="primary"
                            textColor="primary"
                            noCaps
                            unelevated
                            outline
                            dense
                            size="0.9em"
                            padding="2px 10px"
                            :options="options"
                            class="border-toggle"
                            @update:modelValue="getData"
                        />
                    </div>
                </div>
            </div>

            <div class="col-12 col-md AttributeInformationCharts">
                <div
                    v-if="attribute && !emptyRows && !numericAttribute"
                    class="chart-container"
                >
                    <v-chart
                        ref="chart"
                        :option="barChartOptions"
                        class="chart"
                        autoresize
                    />
                </div>
                <div
                    v-else-if="attribute && !emptyRows && numericAttribute"
                    class="chart-container"
                >
                    <v-chart
                        ref="chart"
                        :option="numericAttrOptions"
                        class="chart"
                        autoresize
                    />
                </div>
                <div
                    v-else
                    class="flex flex-center q-mx-md q-my-lg"
                >
                    <WText>
                        {{ $t('visualization.customPlotPanels.attributesInfo.noData') }}
                    </WText>
                </div>

                <div v-if="attribute && !emptyRows && numericAttribute" class="NumericAttr">
                    <div class="row q-col-gutter-md items-center">
                        <div class="col-12 col-lg-7">
                            <div class="text-caption text-primary q-mb-xs">
                                {{ $t('visualization.customPlotPanels.attributesInfo.numericRangeLabel') }}
                            </div>
                            <q-range
                                v-model="numericRange"
                                class="NumericRange"
                                :min="numericAttrMin"
                                :max="numericAttrMax"
                                dense
                                label
                                thumbSize="14px"
                                trackSize="2px"
                                @change="getData(attribute, filters, filterSetsUUIDs, generalOperator)"
                            />
                        </div>

                        <div class="col-12 col-lg-5">
                            <div class="row q-col-gutter-sm NumericMetricsRow">
                                <div class="col-6 col-sm-3">
                                    <div class="NumericMetric"><strong>avg:</strong> {{ avg }}</div>
                                </div>
                                <div class="col-6 col-sm-3">
                                    <div class="NumericMetric"><strong>min:</strong> {{ min }}</div>
                                </div>
                                <div class="col-6 col-sm-3">
                                    <div class="NumericMetric"><strong>max:</strong> {{ max }}</div>
                                </div>
                                <div class="col-6 col-sm-3">
                                    <div class="NumericMetric"><strong>sd:</strong> {{ sd }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import moment from 'moment'
import {
    filtersMixin, validatorsMixin, filtersStorageMixin, dashboardChartsMixin,
} from '@/mixins'
import {
    Api,
    apiRequest,
    notifyError,
} from '@/api'
import echartsColors from '@/charts/echartsColors'

export default {
    name: 'AttributeInformationPanel',
    components: { },
    mixins: [filtersMixin, validatorsMixin, filtersStorageMixin, dashboardChartsMixin],
    inject: ['App'],
    props: {
        currentTitle: VueTypes.object,
        attributes: VueTypes.array,
        categoricalAttributes: VueTypes.array,
        numericAttributes: VueTypes.array,
        processId: VueTypes.string,
        filters: VueTypes.array,
        chart: VueTypes.object,
    },
    emits: ['title', 'chartLoading'],
    data() {
        return {
            title: this.currentTitle,
            selected: false,
            select: null,
            range: '',
            model: null,
            modifiedFilters: [],
            duration: null,
            stringOptions: '',
            metricOptions: [{
                key: 'FD', label: this.$t('visualization.customPlotPanels.frequencyAndDuration'), disabled: false, icon: 'timelapse',
            },
            {
                key: 'AI', label: this.$t('visualization.customPlotPanels.attributeInfo'), disabled: true, icon: 'query_stats',
            }],
            attribute: undefined,
            optionAttributes: [],
            eventAttribute: false,
            activities: [],
            optionActivities: [],
            activitiesTimer: null,
            emptyRows: false,
            maxDur: 'days',
            numericAttribute: false,
            numericRange: { min: 0, max: 0 },
            numericAttrMin: null,
            numericAttrMax: null,
            timeUnit: { unit: 'month', label: this.$t('visualization.customPlotPanels.attributesInfo.month') },
            currentSel: {
                value: 'FREQUENCY',
                label: this.$t('visualization.customPlotPanels.frequency'),
            },
            avg: undefined,
            min: undefined,
            max: undefined,
            sd: undefined,
            barChartOptions: {
                title: {
                    text: '',
                },
                tooltip: {
                    trigger: 'axis',
                    confine: true,
                },
                xAxis: {
                    type: 'category',
                    data: [],
                    axisLabel: {},
                },
                yAxis: [
                    {
                        type: 'value',
                        axisLabel: {
                            formatter: (value) => this.formatDuration(value, 1, 1),
                        },
                    },
                    {
                        type: 'value',
                        axisLabel: {
                            formatter: (value) => this.formatNumber(value),
                        },
                    },
                ],
                dataZoom: [
                    {
                        type: 'inside',
                        show: true,
                        start: 0,
                        end: 100,
                        handleSize: 8,
                    },
                ],
                grid: {
                    left: 45,
                    right: 45,
                    bottom: 23,
                    top: 20,
                },
                series: [
                    {
                        name: this.$t('visualization.customPlotPanels.duration'),
                        type: 'line',
                        data: [],
                        itemStyle: {
                            color: echartsColors.palette.greenAccent,
                        },
                        yAxisIndex: 0,
                    },
                    {
                        name: this.$t('visualization.customPlotPanels.frequency'),
                        type: 'bar',
                        data: [],
                        itemStyle: {
                            color: echartsColors.palette.blueSoft,
                        },
                        yAxisIndex: 1,
                    },
                ],
            },
            numericAttrOptions: {
                tooltip: {
                    trigger: 'axis',
                    confine: true,
                },
                title: {
                    left: 'left',
                    top: '5%',
                    textStyle: {
                        color: echartsColors.palette.blueMid,
                        fontSize: '1em',
                    },
                },
                xAxis: {
                    type: 'category',
                    data: [],
                    axisTick: { show: false },
                    axisLine: {
                        show: false,
                    },
                },
                yAxis: {
                    type: 'value',
                    splitLine: {
                        show: false,
                    },
                    axisLabel: {
                        show: false,
                    },
                },
                grid: {
                    left: 45,
                    right: 15,
                    bottom: '15%',
                    top: 50,
                },
                series: {
                    name: this.$t('visualization.customPlotPanels.frequency'),
                    type: 'bar',
                    data: [],
                    itemStyle: {
                        borderRadius: [3, 3, 3, 3],
                        color: echartsColors.palette.blue[0],
                    },
                },
            },
            optionSel: 'ABSOLUTE',
            options: [
                {
                    value: 'ABSOLUTE',
                    label: 'abs',
                },
                {
                    value: 'PERCENTAGE',
                    label: '%',
                },
            ],
        }
    },
    computed: {
        freqDur() {
            return [{
                value: 'FREQUENCY',
                label: this.$t('visualization.customPlotPanels.frequency'),
            },
            {
                value: 'DURATION',
                label: this.$t('visualization.customPlotPanels.duration'),
            }]
        },
    },
    watch: {
        filters(newValue, oldValue) {
            let equal = false
            if (newValue.length === oldValue.length) {
                if (newValue.length === 0) {
                    equal = true
                } else {
                // eslint-disable-next-line no-plusplus
                    for (let i = 0; i < newValue.length; i++) {
                        if (newValue[i] !== oldValue[i]) {
                            equal = true
                        }
                    }
                }
            }
            if (equal === false) {
                this.getData()
            }
        },
        eventAttribute() {
            this.actiSel()
            this.getData()
        },
        chart() {
            this.getData()
        },
        attribute(newVal) {
            if (this.processId && newVal) {
                localStorage.setItem(`attributePanel.selectedAttribute.${this.processId}`, newVal)
            }
        },
    },
    mounted() {
        this.attrSel()
        this.actiSel()
        const key = `attributePanel.selectedAttribute.${this.processId}`
        const savedAttr = localStorage.getItem(key)
        if (
            savedAttr
            && this.optionAttributes.some((a) => a.name === savedAttr)
        ) {
            this.attribute = savedAttr
            this.getData()
        }
    },
    methods: {
        attrSel() {
            const numericTypes = ['BOOLEAN', 'SHORT', 'INTEGER', 'LONG', 'DECIMAL']
            const isNumericType = (item) => numericTypes.includes(item.type)

            const optionAttr = this.attributes.filter((item) => {
                const isNumericTypeConts = isNumericType(item)
                const isStringAndInCategorical = item.type === 'STRING'
                    && this.categoricalAttributes.some((cat) => cat.name === item.name)
                return isNumericTypeConts || isStringAndInCategorical
            })
            this.resetChart()
            this.optionAttributes = optionAttr
        },
        actiSel() {
            this.resetChart()
            if (!this.chart || !this.chart.activities) {
                this.optionActivities = []
                this.activities = []
                return
            }
            const optionActi = this.chart.activities.filter((item) => !['START', 'END'].includes(item.name))
            this.optionActivities = optionActi.map((item) => item.name).sort()
        },
        resetActivities() {
            this.activities = []
            this.activitiesTimer = setTimeout(() => {
                this.getData()
            }, 500)
        },
        setSelected() {
            this.selected = !this.selected
        },
        handleClickOption(sel) {
            this.$emit('title', sel)
            this.setSelected()
        },
        handleActivitiesInput(selected) {
            if (!this.activities.includes(selected)) {
                this.activities.push(selected)
            } else {
                const index = this.activities.indexOf(selected)
                if (index !== -1) {
                    this.activities.splice(index, 1)
                }
            }
            clearTimeout(this.activitiesTimer)
            this.activitiesTimer = setTimeout(() => {
                this.getData()
            }, 500)
        },
        handleStandardActivitiesInput() {
            clearTimeout(this.activitiesTimer)
            this.activitiesTimer = setTimeout(() => {
                this.getData()
            }, 500)
        },
        getData() {
            const typeAttr = this.optionAttributes.find((obj) => obj.name === this.attribute)
            if (!typeAttr) {
                this.emptyRows = true
                return
            }
            this.$emit('chartLoading', true)
            this.resetChart()
            let data = {
                attribute: this.attribute,
                sortBy: this.currentSel.value,
                limit: 8,
                start: 0,
            }
            if (typeAttr.type === 'STRING' || typeAttr.type === 'BOOLEAN') {
                this.numericAttribute = false
            } else {
                this.numericAttribute = true
            }
            if (this.chart.variants || this.chart.variantId || this.chart.loopId || this.chart.loopId === 0) {
                data = {
                    ...data,
                    ...(this.chart.variantId ? { variantUid: this.chart.variantId } : {}),
                    ...(this.chart.variants ? { variantCount: this.chart.variants } : {}),
                    ...(this.chart.loopId || this.chart.loopId === 0 ? { loopUid: this.chart.loopId } : {}),
                }
            } else {
                this.emptyRows = true
                this.$emit('chartLoading', false)
                return
            }
            if (this.activities.length > 0) {
                data.eventAttribute = true
                data.activities = this.activities
            }
            if (this.numericAttribute) {
                const numAttr = this.numericAttributes.find((a) => a.name === this.attribute)
                this.numericAttrMin = Number(numAttr.values.at(0))
                this.numericAttrMax = Number(numAttr.values.at(1))
                this.numericRange = { min: this.numericAttrMin, max: this.numericAttrMax }
                delete data.limit
                delete data.start
                delete data.sortBy
                data.startValue = this.numericRange.min
                data.endValue = this.numericRange.max
            }
            data = this.buildVisualizationParams(data)
            apiRequest(Api().visualizations.attributePanel({ processId: this.processId, params: data }))
                .then((returnData) => {
                    if (this.numericAttribute) {
                        if (returnData.numericalRows.length === 0) {
                            this.emptyRows = true
                        } else {
                            this.emptyRows = false
                        }
                        this.updateNumericChart(returnData.numericalRows, returnData.data)
                    } else {
                        if (returnData.categoricalRows.length === 0) {
                            this.emptyRows = true
                        } else {
                            this.emptyRows = false
                        }
                        this.updateChart(returnData.categoricalRows)
                    }
                })
                .catch(() => {
                    notifyError()
                    this.resetChart()
                })
                .finally(() => {
                    this.$emit('chartLoading', false)
                })
        },
        resetChart() {
            this.numericAttrOptions.series.data = []
            this.numericAttrOptions.xAxis.data = []
            this.barChartOptions.series[1].data = []
            this.barChartOptions.series[0].data = []
            this.barChartOptions.series[1].name = this.$t('visualization.customPlotPanels.frequency')
            this.barChartOptions.series[0].name = this.$t('visualization.customPlotPanels.duration')
            this.barChartOptions.xAxis.data = []
        },
        updateChart(returnData) {
            this.barChartOptions.yAxis[1].axisLabel = { show: true }
            // this.barChartOptions.yAxis[0].axisLabel = { formatter: value => `${this.formatDuration(value, 1, 1)}` }
            this.barChartOptions.xAxis.axisLabel = {
                formatter: (value) => {
                    const maxLength = 8
                    // eslint-disable-next-line no-restricted-globals
                    if (!isNaN(value) && typeof value === 'number') {
                        return this.formatNumber(value)
                    }
                    if (value.length > maxLength) {
                        return `${value.slice(0, maxLength)}...`
                    }
                    return `${value}`
                },
            }
            this.barChartOptions.tooltip = {
                trigger: 'axis',
                confine: true,
                formatter: (params) => {
                    let tooltipContent = '<div style="max-width: 15em;">'
                    tooltipContent += `${this.lineBreak(params[0].name)}`
                    params.forEach((param) => {
                        const { seriesName } = param
                        const { value } = param
                        const { color } = param
                        // eslint-disable-next-line max-len
                        tooltipContent += `<div style="width: 10px; height: 10px; background-color: ${color};border-radius: 50%; display: inline-block; margin-right: 3px;">
                               </div>`
                        if (seriesName === this.$t('visualization.customPlotPanels.duration')) {
                            tooltipContent += `${seriesName}: ${moment.duration(value, 'seconds').format('d[d] h[h]:m[m]:s[s]', { largest: 2, trim: false })} <br>`
                        } else {
                            tooltipContent += `${seriesName}: ${value} <br>`
                        }
                        tooltipContent += '</div>'
                    })
                    return tooltipContent
                },
            }
            if (this.optionSel === 'ABSOLUTE') {
                const frequencyArr = [returnData.map((item) => item.frequency)][0]
                this.barChartOptions.series[1].data = frequencyArr
                this.barChartOptions.series[1].name += ' (abs)'
            } else {
                const percentageArr = [returnData.map((item) => item.percentage)][0]
                this.barChartOptions.series[1].data = percentageArr
                this.barChartOptions.series[1].name += ' (%)'
            }
            const commonAttributes = [returnData.map((item) => item.attribute)][0]
            this.barChartOptions.series[0].data = returnData.map((item) => item.duration)
            this.barChartOptions.xAxis.data = commonAttributes
        },
        updateNumericChart(rows, data) {
            if (data) {
                const dataRows = rows.map((item) => ({
                    value: item.frequency,
                }))
                this.numericAttrOptions.series.data = dataRows
                const rangosConvertidos = rows.map((elemento) => `${elemento.start} - ${elemento.finish}`)
                this.numericAttrOptions.xAxis.data = rangosConvertidos
                this.avg = this.App.numberLocationFormat(this.formatNumber(data.avg), false, true)
                this.max = this.App.numberLocationFormat(this.formatNumber(data.max), false, true)
                this.min = this.App.numberLocationFormat(this.formatNumber(data.min), false, true)
                this.sd = this.App.numberLocationFormat(this.formatNumber(data.sd), false, true)
            }
        },
        lineBreak(inputString) {
            let resultado = ''
            for (let i = 0; i < inputString.length; i += 22) {
                const linea = inputString.substr(i, 22)
                resultado += `${linea }<br>`
            }
            return resultado
        },
        getActivities() {
            if (this.chart.activities) {
                this.chartActivities = [this.chart.activities.map((activity) => activity.name)]
            }
        },
        checkDate(value) {
            if (this.timeUnit.unit === 'day') {
                return moment(value).format('DD/MM/YY')
            }
            if (this.timeUnit.unit === 'month') {
                return moment(value).format('MM/YYYY')
            }
            return value
        },
    },
}// eslint-disable-next-line
</script>
<style lang="scss" scoped>
.AttributeInformation {
  position: relative;
    max-width: none;
}

.AttributeInformationOptions {
        width: 250px;
        max-width: 280px;
}

.AttributeInformationCharts {
        width: auto;
    min-width: 0;
}

.NumericRange {
    width: 100%;
    margin: 0;
}

.NumericMetricsRow {
    align-items: center;
}

.NumericMetric {
    font-size: 0.8rem;
    color: $gray-01;
    white-space: nowrap;
}

.NumericMetric strong {
    color: $primary;
}

.attrSelect {
    width: 210px;
    height: auto;
    margin: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.chart-container {
    overflow: auto;
    width: 100%;

    .chart {
        width: 100%;
        height: 160px;

        &+.chart {
            margin-left: 30px;

            @media screen and (max-width: $lg) {
                margin-left: 0px;
            }
        }
    }

    .openIcon {
        height: fit-content;
        padding-bottom: 0px;
        margin-left: 0 auto;
        cursor: pointer;
    }
}
.Field {
    width: 210px;
    height: auto;
    margin: 2px;
    &:deep(.q-field__control) {
            height: 50px;
            .q-field__control-container .q-field__native span {
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    line-height: 34px;
            }
            .q-field__append {
                height: 50px;
            }
    }
}
.timeUnitField {
    width: fit-content;
    min-width: 150px;
    height: auto;
    margin: 2em;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
}
/* Selector específico para aplicar solo a Dropdown */
:deep(.Dropdown .Items) {
    gap: 10px;
    white-space: nowrap;
    flex-wrap: nowrap;
}

:deep(.Dropdown .Items .LeftIcon) {
    margin-right: 0;
    display: flex;
    flex-direction: column;
}

:deep(.Dropdown .Items .Text) {
    align-items: center;
    display: flex;
    flex-direction: column;
}

:deep(.border-toggle .q-btn[aria-pressed="true"]) {
    background: rgba($primary, 0.12) !important;
}

:deep(.border-toggle.q-btn-group--outline:before),
:deep(.border-toggle .q-btn--outline:before) {
    border-width: 1px !important;
}

</style>
